import React from 'react';
import { TrendingUp, Users, Clock, Calendar, Target, Eye, ArrowRight } from 'lucide-react';
import { useAnalyticsStore } from '../../../stores/analyticsStore';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { useCalendarStore } from '../../../stores/calendarStore';
import { useUserStore } from '../../../stores/userStore';
import GoalProgressWidget from './GoalProgressWidget';

export default function StatsOverview() {
  const { entries } = useAnalyticsStore();
  const { plans } = useContentPlanStore();
  const { events } = useCalendarStore();
  const userGoals = useUserStore(state => state.getUserGoals());
  
  // Calculate total engagement from analytics entries
  const totalEngagement = entries.reduce((sum, entry) => {
    return sum + entry.likes + entry.comments;
  }, 0);
  
  // Get upcoming events (next 7 days)
  const getUpcomingEvents = () => {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);
    
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate >= today && eventDate <= nextWeek;
    });
  };
  
  const upcomingEvents = getUpcomingEvents();
  
  // Calculate time saved (estimated)
  const timeSaved = (plans.length * 3) + (entries.length * 0.5) + (events.length * 0.25);

  // Format number with k/M/B suffix
  const formatNumberWithSuffix = (num: number): string => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02]">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Engajamento Total</p>
            <p className="text-2xl font-bold text-gray-900">{formatNumberWithSuffix(totalEngagement)}</p>
          </div>
        </div>
        <div className="mt-4 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 rounded-full" style={{ width: `${Math.min(totalEngagement / 100, 100)}%` }}></div>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02]">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <Calendar className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Eventos Planejados</p>
            <p className="text-2xl font-bold text-gray-900">{upcomingEvents.length}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm">
          <span className="text-blue-600 font-medium">{upcomingEvents.length} eventos</span>
          <span className="text-gray-500 ml-1">nos próximos 7 dias</span>
        </div>
      </div>
      
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02]">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Tempo Economizado</p>
            <p className="text-2xl font-bold text-gray-900">{Math.round(timeSaved)}h</p>
          </div>
        </div>
        <div className="mt-4 flex items-center text-sm">
          <span className="text-amber-600 font-medium">{Math.round(timeSaved)} horas</span>
          <span className="text-gray-500 ml-1">de trabalho</span>
        </div>
      </div>
      
      {userGoals && (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all transform hover:scale-[1.02]">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Seus Objetivos</p>
                <p className="text-lg font-medium text-gray-900">@{userGoals.username}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            {/* Followers Progress */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700">Seguidores</span>
                </div>
                <span className="text-xs text-gray-600 tabular-nums">
                  {formatNumberWithSuffix(userGoals.followers)} / {formatNumberWithSuffix(userGoals.targetFollowers)}
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-indigo-600 rounded-full transition-all"
                  style={{ width: `${Math.min(Math.round((userGoals.followers / userGoals.targetFollowers) * 100), 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Views Progress */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-1">
                  <Eye className="h-4 w-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700">Visualizações</span>
                </div>
                <span className="text-xs text-gray-600 tabular-nums">
                  {formatNumberWithSuffix(Math.round(userGoals.followers * 2.5))} / {formatNumberWithSuffix(userGoals.targetViews)}
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${Math.min(Math.round((userGoals.followers * 2.5 / userGoals.targetViews) * 100), 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-100">
            <button 
              onClick={() => {
                // This will be handled by the parent component
                document.dispatchEvent(new CustomEvent('updateMetrics'));
              }}
              className="text-indigo-600 hover:text-indigo-700 text-xs font-medium flex items-center justify-center w-full"
            >
              Atualizar Métricas
              <ArrowRight className="ml-1 h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}